/* ==========================================================================
   THE LEAD SET TILE CYCLES ITS COLOURWAY

   There is ONE set of images on the tile and this changes what they point at.

   The first attempt stacked a full copy of the tile per colourway and faded
   between them. It could not be made to line up: the tile is a flex row whose
   children size themselves in percentages of it, and a copy living in an
   absolutely positioned layer resolves those percentages against a different
   box - measured, the companions came out 186px on the base row and 154px in
   the layer, so every change visibly jumped. Swapping the source on the images
   that are already there makes the layout identical by construction, because
   it IS the same layout.

   The extra colourways still ship as markup: they are the URL list, and having
   the browser fetch them means a swap is instant rather than a blank frame.

   Nothing is created or resized per change, so this cannot cause layout shift.
   On screen is answered by measuring the tile rather than by an
   IntersectionObserver, whose first callback could stop the timer that had
   just started and leave nothing to restart it. It holds while the tab is
   hidden and while the shopper is pointing at the tile, and it is NOT gated on
   prefers-reduced-motion - that guard removed the feature outright on any
   machine with animations turned off. The stylesheet drops the crossfade to an
   instant swap under that setting instead.
   ========================================================================== */
(function () {
  "use strict";

  var EVERY = 4200;
  var FADE = 220;
  var MARGIN = 120;
  /* Reduced motion removes the CROSSFADE, not the rotation. That matters for
     the timing too: with the transition off, fading out still empties the tile
     instantly and holding it there for the fade duration would be a blink on a
     white tile. So under that setting the swap is immediate and nothing is
     ever hidden. */
  var REDUCE = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);

  /* src is NOT enough. The case image carries a srcset, and a browser that has
     chosen a candidate from it ignores a changed src entirely - which is why
     the companions turned burgundy and the phone stayed black in every
     screenshot. Both attributes travel together. */
  function shotOf(im) {
    return im ? { src: im.getAttribute("src"), set: im.getAttribute("srcset") } : null;
  }
  function srcsOf(root) {
    var main = root.querySelector(":scope > img");
    var pieces = [].slice.call(root.querySelectorAll(".gc-clinks__setpiece img"));
    return [main].concat(pieces).map(shotOf);
  }

  function wire(media) {
    var layers = [].slice.call(media.querySelectorAll("[data-gc-clinks-rot]"));
    if (!layers.length) return;

    /* the elements that actually show, and every colourway they can show */
    var targets = [media.querySelector(":scope > img")]
      .concat([].slice.call(media.querySelectorAll(":scope > .gc-clinks__set .gc-clinks__setpiece img")));
    if (!targets[0]) return;

    var sets = [targets.map(shotOf)];
    layers.forEach(function (l) { sets.push(srcsOf(l)); });

    var total = sets.length;
    var at = 0;
    var held = false;
    var swapping = false;

    /* ---- the indicator: one track per colourway, the live one filling ---- */
    var dots = document.createElement("span");
    dots.className = "gc-clinks__dots";
    dots.setAttribute("aria-hidden", "true");
    var marks = [];
    for (var d = 0; d < total; d++) {
      var slot = document.createElement("span");
      slot.className = "gc-clinks__dot";
      slot.appendChild(document.createElement("i"));
      dots.appendChild(slot);
      marks.push(slot);
    }
    dots.style.setProperty("--rot-ms", EVERY + "ms");
    media.appendChild(dots);

    function mark(n) {
      for (var i = 0; i < marks.length; i++) {
        if (i !== n) { marks[i].classList.remove("is-on"); continue; }
        /* off, reflow, on - which is what restarts the fill from zero */
        marks[i].classList.remove("is-on");
        void marks[i].offsetWidth;
        marks[i].classList.add("is-on");
      }
    }

    function paint(n) {
      var shots = sets[n];
      for (var i = 0; i < targets.length; i++) {
        var shot = shots[i];
        if (!shot || !shot.src) continue;
        /* srcset first, then src: with the candidate list already replaced the
           browser resolves the new src against the new list in one step */
        if (shot.set) targets[i].setAttribute("srcset", shot.set);
        else targets[i].removeAttribute("srcset");
        targets[i].setAttribute("src", shot.src);
      }
    }

    function inView() {
      var r = media.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return false;
      return r.bottom > -MARGIN && r.top < (window.innerHeight || 0) + MARGIN;
    }

    /* Decode the NEXT colourway before touching anything on screen.

       Setting src on three images at once does not make them appear at once:
       the two small companions decode before the large case does, so the tile
       showed a black case beside burgundy companions for a frame or two -
       caught in a screenshot twice. Decoding into the browser cache first,
       then assigning, means all three paint from a bitmap that is already
       ready. It also means the swap needs no hiding at all, which is what
       makes an instant change safe when animations are turned off. */
    var decoded = {};
    function preload(n) {
      if (decoded[n]) return decoded[n];
      var warm = Promise.all(sets[n].map(function (shot) {
        if (!shot || !shot.src) return Promise.resolve();
        var im = new Image();
        /* A PLAIN src, deliberately. decode() on a detached Image that also
           carries a srcset never settles - measured here, still pending after
           four seconds - and because the swap waits on this, one hung promise
           left swapping true and stopped the rotation for good. */
        im.src = shot.src;
        return im.decode ? im.decode().catch(function () {}) : Promise.resolve();
      }));
      /* and even so, this can never be what stops the tile turning */
      decoded[n] = Promise.race([
        warm,
        new Promise(function (res) { window.setTimeout(res, 900); })
      ]);
      return decoded[n];
    }

    function tick() {
      if (held || swapping || document.hidden || !inView()) return;
      swapping = true;
      var next = (at + 1) % total;
      preload(next).then(function () {
        /* conditions can change while the images are being decoded */
        if (held || document.hidden || !inView()) { swapping = false; return; }
        at = next;
        mark(at);
        if (REDUCE) { paint(at); swapping = false; return; }
        media.classList.add("is-swapping");
        window.setTimeout(function () {
          paint(at);
          media.classList.remove("is-swapping");
          swapping = false;
        }, FADE);
      });
    }

    /* have the one after this ready before it is needed */
    preload(1 % total);

    media.addEventListener("mouseenter", function () { held = true; });
    media.addEventListener("mouseleave", function () { held = false; });
    var link = media.closest("a");
    if (link) {
      link.addEventListener("focus", function () { held = true; });
      link.addEventListener("blur", function () { held = false; });
    }

    mark(0);
    window.setInterval(tick, EVERY);
  }

  function boot() {
    var seen = [];
    [].slice.call(document.querySelectorAll("[data-gc-clinks-rot]")).forEach(function (l) {
      var media = l.parentElement;
      if (!media || seen.indexOf(media) !== -1) return;
      seen.push(media);
      wire(media);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
